import { Icon } from '@iconify/react/dist/iconify.js';
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import CourseService from '../../../Api/CourseService';

const AddCourse = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const courseId = new URLSearchParams(location.search).get('id');
    const CourseAPI = new CourseService();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [courseData, setCourseData] = useState({
        course: {
            title: '',
            description: '',
            price: 0,
            thumbnail_url: 'https://www.contentviewspro.com/wp-content/uploads/2017/07/default_image.png',
            max_students: 0,
            validity_days: 0,
            session_points: 0,
            type: 'Beginner', // <-- Add default type
            caution: ''
        },
        categories: [],
        files: [] // Now using file_name, file_url, description, display_order
    });

    const [imagePreviewUrl, setImagePreviewUrl] = useState('');

    // Fetch course data if in edit mode
    useEffect(() => {
        const fetchCourse = async () => {
            if (courseId) {
                try {
                    setIsLoading(true);
                    const response = await CourseAPI.GetById(courseId);
                    if (response.data && response.data.data) {
                        const courseData = response.data.data;
                        // Map files to new structure
                        const mappedFiles = (courseData.files || []).map((file, idx) => ({
                            fileName: file.file_name || '',
                            file_url: file.file_url || '',
                            googleDriveUrl: file.file_url || '',
                            description: file.description || '',
                            display_order: file.display_order || idx + 1
                        }));
                        setCourseData({
                            course: {
                                title: courseData.title || '',
                                description: courseData.description || '',
                                price: courseData.price || 0,
                                thumbnail_url: courseData.thumbnail_url || 'https://www.contentviewspro.com/wp-content/uploads/2017/07/default_image.png',
                                max_students: courseData.max_students || 0,
                                validity_days: courseData.validity_days || 0,
                                session_points: courseData.session_points || 0,
                                type: courseData.type || 'Beginner', // <-- Set type from loaded data
                                caution: courseData.caution || ''
                            },
                            categories: courseData.categories || [],
                            files: mappedFiles
                        });
                    }
                } catch (err) {
                    setError('Failed to load course data. Please try again.');
                    console.error('Error loading course:', err);
                } finally {
                    setIsLoading(false);
                }
            }
        };

        fetchCourse();
    }, [courseId]);

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreviewUrl(reader.result);
                setCourseData(prev => ({
                    ...prev,
                    course: {
                        ...prev.course,
                        thumbnail_url: reader.result
                    }
                }));
            };
            reader.readAsDataURL(file);
        }
    };

    const handleCourseChange = (e) => {
        const { name, value } = e.target;
        setCourseData(prev => ({
            ...prev,
            course: {
                ...prev.course,
                [name]: value
            }
        }));
    };

    const addCategory = () => {
        setCourseData(prev => ({
            ...prev,
            categories: [...prev.categories, { name: '', videos: [] }]
        }));
    };

    const handleCategoryChange = (index, value) => {
        setCourseData(prev => {
            const newCategories = [...prev.categories];
            newCategories[index] = { ...newCategories[index], name: value };
            return { ...prev, categories: newCategories };
        });
    };

    const addVideo = (categoryIndex) => {
        setCourseData(prev => {
            const newCategories = [...prev.categories];
            newCategories[categoryIndex].videos.push({
                title: '',
                vimeo_url: '',
                video_duration: 0
            });
            return { ...prev, categories: newCategories };
        });
    };

    const handleVideoChange = (categoryIndex, videoIndex, field, value) => {
        setCourseData(prev => {
            const newCategories = [...prev.categories];
            newCategories[categoryIndex].videos[videoIndex] = {
                ...newCategories[categoryIndex].videos[videoIndex],
                [field]: value
            };
            return { ...prev, categories: newCategories };
        });
    };

    const removeCategory = (categoryIndex) => {
        setCourseData(prev => ({
            ...prev,
            categories: prev.categories.filter((_, index) => index !== categoryIndex)
        }));
    };

    const removeVideo = (categoryIndex, videoIndex) => {
        setCourseData(prev => {
            const newCategories = [...prev.categories];
            newCategories[categoryIndex].videos = newCategories[categoryIndex].videos.filter((_, index) => index !== videoIndex);
            return { ...prev, categories: newCategories };
        });
    };

    const addFile = () => {
        setCourseData(prev => ({
            ...prev,
            files: [
                ...prev.files,
                { file_name: '', googleDriveUrl: '', description: '', display_order: prev.files.length + 1 }
            ]
        }));
    };

    const handleFileChange = (index, field, value) => {
        setCourseData(prev => {
            const newFiles = [...prev.files];
            newFiles[index] = { ...newFiles[index], [field]: value };
            return { ...prev, files: newFiles };
        });
    };

    const removeFile = (index) => {
        setCourseData(prev => {
            const filtered = prev.files.filter((_, i) => i !== index);
            // Recalculate display_order
            const reordered = filtered.map((file, idx) => ({ ...file, display_order: idx + 1 }));
            return { ...prev, files: reordered };
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);

        try {
            // Validate categories have videos
            if (courseData.categories.length === 0) {
                throw new Error('Please add at least one category');
            }

            for (const category of courseData.categories) {
                if (!category.name.trim()) {
                    throw new Error('Category name cannot be empty');
                }
                if (category.videos.length === 0) {
                    throw new Error(`Please add at least one video to category: ${category.name}`);
                }

                for (const video of category.videos) {
                    if (!video.title.trim() || !video.vimeo_url.trim()) {
                        throw new Error('Video title and URL are required for all videos');
                    }
                }
            }

            // Prepare files in correct format
            const files = courseData.files.map((file, idx) => ({
                file_name: file.file_name,
                googleDriveUrl: file.file_url,
                description: file.description,
                display_order: file.display_order || idx + 1
            }));

            let response;
            if (courseId) {
                // Map files to old structure for update
                const oldFiles = courseData.files.map(file => ({
                    fileName: file.file_name,
                    googleDriveUrl: file.file_url,
                    description: file.description
                }));
                response = await CourseAPI.UpdateByID({ ...courseData, files: oldFiles }, courseId);
            } else {
                // Create new course with new structure
                response = await CourseAPI.AddCourse({ ...courseData, files });
            }

            if (response.data.status === "success") {
                navigate('/course-list');
            } else {
                throw new Error(response.data.message || 'Failed to save course');
            }
        } catch (error) {
            setError(error.response?.data?.message || error.message || 'Failed to save course');
            console.error('Course save error:', error);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="card h-100 p-0 radius-12">
            <div className="card-body">
                <h4 className="text-xl text-primary-light mb-24">
                    {courseId ? 'Edit Course' : 'Add New Course'}
                </h4>
                {error && (
                    <div className="alert alert-danger mb-3" role="alert">
                        {error}
                    </div>
                )}
                {isLoading && !courseData.course.title ? (
                    <div className="text-center p-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit}>
                        <div className="row g-4">
                            <div className="col-md-4">

                               {courseData.course.thumbnail_url && (
                                    <div className="mt-3">
                                        <img 
                                            src={courseData.course.thumbnail_url} 
                                            alt="Course thumbnail" 
                                            className="img-fluid rounded"
                                            style={{ width: '100%', height: '200px', objectFit: 'cover' }}
                                        />
                                    </div>
                                )}

                                <div className="mb-3">
                                    <label className="form-label">
                                        Thumbnail URL <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="url"
                                        className="form-control"
                                        name="thumbnail_url"
                                        value={courseData.course.thumbnail_url}
                                        onChange={handleCourseChange}
                                        placeholder="Enter thumbnail URL"
                                        required
                                    />
                                </div>

                                <div className="mb-3">
                                    <label className="form-label">
                                        Price <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        name="price"
                                        value={courseData.course.price}
                                        onChange={handleCourseChange}
                                        placeholder="0"
                                        required
                                    />
                                </div>

                                <div className="mb-3">
                                    <label className="form-label">
                                        Max Students <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        name="max_students"
                                        value={courseData.course.max_students}
                                        onChange={handleCourseChange}
                                        placeholder="0"
                                        required
                                    />
                                </div>

                                <div className="mb-3">
                                    <label className="form-label">
                                        Type <span className="text-danger">*</span>
                                    </label>
                                    <select
                                        className="form-control"
                                        name="type"
                                        value={courseData.course.type}
                                        onChange={handleCourseChange}
                                        required
                                    >
                                        <option value="Beginner">Beginner</option>
                                        <option value="Advance">Advance</option>
                                        <option value="Expert">Expert</option>
                                    </select>
                                </div>
                            </div>

                            <div className="col-md-8">
                                <div className="mb-3">
                                    <label className="form-label">
                                        Course Title <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="title"
                                        value={courseData.course.title}
                                        onChange={handleCourseChange}
                                        placeholder="Enter course title"
                                        required
                                    />
                                </div>

                                <div className="mb-3">
                                    <label className="form-label">
                                        Description <span className="text-danger">*</span>
                                    </label>
                                    <textarea
                                        className="form-control"
                                        name="description"
                                        value={courseData.course.description}
                                        onChange={handleCourseChange}
                                        placeholder="Enter course description"
                                        rows="4"
                                        required
                                    />
                                </div>

                                <div className="row g-3 mb-3">
                                    <div className="col-md-6">
                                        <label className="form-label">
                                            Validity (days) <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            name="validity_days"
                                            value={courseData.course.validity_days}
                                            onChange={handleCourseChange}
                                            placeholder="0"
                                            required
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">
                                            Session Points <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            name="session_points"
                                            value={courseData.course.session_points}
                                            onChange={handleCourseChange}
                                            placeholder="0"
                                            required
                                        />
                                    </div>
                                </div>

                                <div className="mb-3">
                                    <label className="form-label">
                                        Caution
                                    </label>
                                    <textarea
                                        className="form-control"
                                        name="caution"
                                        value={courseData.course.caution}
                                        onChange={handleCourseChange}
                                        placeholder="Enter caution message"
                                        rows="3"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="mt-4">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h6 className="mb-0">Categories</h6>
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={addCategory}
                                >
                                    + Add Category
                                </button>
                            </div>

                            <div className="row g-3">
                                {courseData.categories.map((category, categoryIndex) => (
                                    <div key={categoryIndex} className="col-md-6 mb-16">
                                        <div className="card border h-100">
                                            <div className="card-body">
                                                <div className="d-flex justify-content-between align-items-center mb-16">
                                                    <input
                                                        type="text"
                                                        className="form-control radius-8"
                                                        value={category.name}
                                                        onChange={(e) => handleCategoryChange(categoryIndex, e.target.value)}
                                                        placeholder="Category Name"
                                                        required
                                                    />
                                                    <button
                                                        type="button"
                                                        className="btn btn-danger btn-sm ms-8"
                                                        onClick={() => removeCategory(categoryIndex)}
                                                    >
                                                        <Icon icon="mdi:trash" />
                                                    </button>
                                                </div>

                                                {/* Videos Section */}
                                                <div>
                                                    <div className="d-flex justify-content-between align-items-center mb-16">
                                                        <h6 className="text-sm text-primary-light">Videos</h6>
                                                        <button
                                                            type="button"
                                                            className="btn btn-outline-primary btn-sm"
                                                            onClick={() => addVideo(categoryIndex)}
                                                        >
                                                            <Icon icon="ic:baseline-plus" /> Add Video
                                                        </button>
                                                    </div>

                                                    {category.videos.map((video, videoIndex) => (
                                                        <div key={videoIndex} className="card border mb-16">
                                                            <div className="card-body">
                                                                <div className="row">
                                                                    <div className="col-11">
                                                                        <div className="mb-16">
                                                                            <input
                                                                                type="text"
                                                                                className="form-control radius-8"
                                                                                value={video.title}
                                                                                onChange={(e) => handleVideoChange(categoryIndex, videoIndex, 'title', e.target.value)}
                                                                                placeholder="Video Title"
                                                                                required
                                                                            />
                                                                        </div>
                                                                        <div className="mb-16">
                                                                            <input
                                                                                type="text"
                                                                                className="form-control radius-8"
                                                                                value={video.vimeo_url}
                                                                                onChange={(e) => handleVideoChange(categoryIndex, videoIndex, 'vimeo_url', e.target.value)}
                                                                                placeholder="Vimeo URL"
                                                                                required
                                                                            />
                                                                        </div>
                                                                        <div className="mb-16">
                                                                            <input
                                                                                type="number"
                                                                                className="form-control radius-8"
                                                                                value={video.video_duration}
                                                                                onChange={(e) => handleVideoChange(categoryIndex, videoIndex, 'video_duration', parseInt(e.target.value))}
                                                                                placeholder="Duration (seconds)"
                                                                                required
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                    <div className="col-1">
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-danger btn-sm"
                                                                            onClick={() => removeVideo(categoryIndex, videoIndex)}
                                                                        >
                                                                            <Icon icon="mdi:trash" />
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="mt-4">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h6 className="mb-0">Files</h6>
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={addFile}
                                >
                                    + Add File
                                </button>
                            </div>
                            <div className="row g-3">
                                {courseData.files.map((file, fileIndex) => (
                                    <div key={fileIndex} className="col-md-6 mb-16">
                                        <div className="card border h-100">
                                            <div className="card-body">
                                                <div className="d-flex justify-content-between align-items-center mb-16">
                                                    <input
                                                        type="text"
                                                        className="form-control radius-8 mb-2"
                                                        value={file.file_name}
                                                        onChange={e => handleFileChange(fileIndex, 'file_name', e.target.value)}
                                                        placeholder="File Name"
                                                        required
                                                    />
                                                    <button
                                                        type="button"
                                                        className="btn btn-danger btn-sm ms-8"
                                                        onClick={() => removeFile(fileIndex)}
                                                    >
                                                        <Icon icon="mdi:trash" />
                                                    </button>
                                                </div>
                                                <input
                                                    type="url"
                                                    className="form-control radius-8 mb-2"
                                                    value={file.googleDriveUrl}
                                                    onChange={e => handleFileChange(fileIndex, 'googleDriveUrl', e.target.value)}
                                                    placeholder="File URL"
                                                    required
                                                />
                                                <textarea
                                                    className="form-control radius-8 mb-2"
                                                    value={file.description}
                                                    onChange={e => handleFileChange(fileIndex, 'description', e.target.value)}
                                                    placeholder="Description"
                                                    rows="2"
                                                />
                                                <input
                                                    type="number"
                                                    className="form-control radius-8"
                                                    value={file.display_order}
                                                    onChange={e => handleFileChange(fileIndex, 'display_order', parseInt(e.target.value) || 1)}
                                                    placeholder="Display Order"
                                                    min={1}
                                                    required
                                                />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="d-flex align-items-center justify-content-center gap-3 mt-32">
                            <button
                                type="button"
                                className="border border-danger-600 bg-hover-danger-200 text-danger-600 text-md px-56 py-11 radius-8"
                                onClick={() => navigate(-1)}
                                disabled={isLoading}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="btn btn-primary border border-primary-600 text-md px-56 py-12 radius-8"
                                disabled={isLoading}
                            >
                                {isLoading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                        {courseId ? 'Updating...' : 'Saving...'}
                                    </>
                                ) : (
                                    courseId ? 'Update Course' : 'Save Course'
                                )}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

export default AddCourse;